/** Copied and adapted from MyTrip https://dev.azure.com/AlliedPilots/Services/_git/APA.MyTrip.UI?path=/App/ClientApp/src/app/shared/services/api-cache.service.ts */
import { HttpClient, HttpErrorResponse } from "@angular/common/http";
import { inject, Injectable } from "@angular/core";

import { DateTime } from "luxon";

import { Observable, catchError, map, of, share, startWith } from "rxjs";

import {
  PostConfig,
  RequestConfig,
  RequestState,
  RequestStatus,
  TimeStamped,
} from "@shared/models";
import { ErrorHandlingService } from "./error-handling.service";
import { MessageType } from "@shared/enums";

/**
 * Service that provides functionality for setting/retrieving cached API responses or triggering
 * network request as needed.
 */
@Injectable({
  providedIn: "root",
})
export class ApiCacheService {
  http = inject(HttpClient);
  errorHandlingService = inject(ErrorHandlingService);

  private readonly MAX_CACHE_AGE_DAYS = 1;
  private readonly MAX_CACHE_AGE_MINUTES = 15;

  /**
   * Returns a cached response immediately if one exists. If the cached response
   * has expired, a network request will also be initiated. If a postBody is provided,
   * POST will be used instead of GET.
   * @template T Response type
   * @param {RequestConfig} config  {@link RequestConfig} object for the request.
   * @returns {Observable<RequestState<T>>} Observable that emits a {@link RequestState} object.
   */
  request<T extends TimeStamped>(
    config: RequestConfig,
  ): Observable<RequestState<T>> {
    const cachedResponse = this.getCached<T>(config.cacheId);

    if (cachedResponse && !config.forceRefresh) {
      if (
        cachedResponse.timestamp &&
        DateTime.fromISO(cachedResponse.timestamp, { zone: "utc" }).diffNow(
          "minutes",
        ).minutes *
          -1 <=
          config.cacheValidMinutes
      ) {
        return of(
          new RequestState<T>(
            RequestStatus.IsCompleteWithResponse,
            cachedResponse,
          ),
        );
      }
    }
    return this.forceRefresh<T>(config);
  }

  /**
   * Returns a cached response immediately if one exists. If the cached response
   * has expired, a network request will also be initiated. If a postBody is provided,
   * POST will be used instead of GET.
   * @template T Response type
   * @param {PostConfig} config  {@link PostConfig} object for the request.
   * @returns {Observable<RequestState<T>>} Observable that emits a {@link RequestState} object
   */
  post<T extends TimeStamped>(config: PostConfig): Observable<RequestState<T>> {
    const cachedResponse = this.getCached<T>(config.cacheId);
    const post = this.http.post<T>(config.url, config.postBody);

    if (cachedResponse) {
      if (
        cachedResponse.timestamp &&
        DateTime.fromISO(cachedResponse.timestamp, { zone: "utc" }).diffNow(
          "minutes",
        ).minutes *
          -1 <=
          config.cacheValidMinutes
      ) {
        return of(
          new RequestState<T>(
            RequestStatus.IsCompleteWithResponse,
            cachedResponse,
          ),
        );
      }
    }

    return post.pipe(
      map((response) => {
        if (response) {
          this.cacheResponse(config.cacheId, response, config.arrayKey);
        }
        const newCachedResponse = this.getCached<T>(config.cacheId);
        return new RequestState<T>(
          RequestStatus.IsCompleteWithResponse,
          newCachedResponse,
        );
      }),
      catchError((err, caught) =>
        this.handleError(err, config, cachedResponse, caught),
      ),
      startWith(
        new RequestState<T>(
          RequestStatus.IsLoadingWithCachedResponse,
          cachedResponse,
        ),
      ),
      share(),
    );
  }

  /**
   * Disregards cache expiration and triggers API call. Still immediately returns cached value
   * while loading. Also returns cached value on failure.
   * @param {RequestConfig} config {@link RequestConfig} object for the request.
   * @returns {Observable<RequestState<T>>} Observable that emits a {@link RequestState} object.
   */
  forceRefresh<T extends TimeStamped>(
    config: RequestConfig,
  ): Observable<RequestState<T>> {
    const cachedResponse = this.getCached<T>(config.cacheId);
    const request = this.http.get<T>(config.url);

    return request.pipe(
      map((response) => {
        if (response) {
          this.cacheResponse(config.cacheId, response, config.arrayKey);
        }
        const newCachedResponse = this.getCached<T>(config.cacheId);
        return new RequestState<T>(
          RequestStatus.IsCompleteWithResponse,
          newCachedResponse,
        );
      }),
      catchError((err, caught) =>
        this.handleError(err, config, cachedResponse, caught),
      ),
      startWith(
        new RequestState<T>(
          RequestStatus.IsLoadingWithCachedResponse,
          cachedResponse,
        ),
      ),
      share(),
    );
  }

  /** Clear local storage items that are older than the specified age. */
  private clearOldCache(): void {
    const keys = Object.keys(localStorage);

    for (const key of keys) {
      const item = localStorage.getItem(key);
      if (item) {
        try {
          const parsedItem: TimeStamped = JSON.parse(item);
          if (parsedItem.timestamp) {
            const ageInMinutes =
              DateTime.fromISO(parsedItem.timestamp, { zone: "utc" }).diffNow(
                "minutes",
              ).minutes * -1;
            const ageInDays = ageInMinutes / 1440;

            if (
              (key.startsWith("updates/") ||
                key.startsWith("sequence/") ||
                key.startsWith("sequence-history-activity")) &&
              ageInMinutes >= this.MAX_CACHE_AGE_MINUTES
            ) {
              // console.log(`Removing ${key}: ${ageInMinutes.toFixed(0)} minutes.`);
              localStorage.removeItem(key);
            } else if (ageInDays >= this.MAX_CACHE_AGE_DAYS) {
              // console.log(`Removing ${key}: ${ageInDays.toFixed(2)} days.`);
              localStorage.removeItem(key);
            }
          }
        } catch {
          continue;
        }
      }
    }
  }
  /** Starts a timer to clear old cache every minute. */
  startCacheClearTimer(): void {
    this.clearOldCache();
    setInterval(() => {
      this.clearOldCache();
    }, 60000);
  }
  /**
   * Gets the cached response if found. Returns null if not found.
   * @template T Response type.
   * @param {string} cacheId Tnique string to identify response.
   * @returns {T | null}
   */
  private getCached<T>(cacheId: string): T | null {
    const cached = localStorage.getItem(cacheId);

    if (!cached) {
      return null;
    }

    return JSON.parse(cached) as T;
  }

  /** Saves response to cache. */
  private cacheResponse<T extends TimeStamped>(
    cacheId: string,
    response: T,
    arrayKey?: string,
  ): void {
    if (arrayKey) {
      const cacheObject = {
        [arrayKey]: response,
        timestamp: DateTime.now().toISO(),
      };
      localStorage.setItem(cacheId, JSON.stringify(cacheObject));
    } else {
      response.timestamp = DateTime.now().toISO();
      localStorage.setItem(cacheId, JSON.stringify(response));
    }
  }

  private handleError<T extends TimeStamped>(
    error: HttpErrorResponse,
    requestConfig: RequestConfig | PostConfig,
    cachedResponse?: T | null,
    caught?: Observable<RequestState<T>>,
    response?: T | null,
  ): Observable<RequestState<T>> {
    if (error.status === 404) {
      if (cachedResponse) {
        return of(
          new RequestState<T>(
            RequestStatus.IsCompleteWithResponse,
            cachedResponse,
          ),
        );
      } else {
        if (requestConfig.arrayKey !== "activityRecord") {
          this.errorHandlingService.sendMessage(MessageType.NotFound);
        }
        return of(new RequestState<T>(RequestStatus.IsCompleteNoResponse));
      }
    } else if (!window.navigator.onLine) {
      // Don't show errors if offline.
      if (cachedResponse) {
        return of(
          new RequestState<T>(
            RequestStatus.IsCompleteWithResponse,
            cachedResponse,
          ),
        );
      } else {
        return of(new RequestState<T>(RequestStatus.IsCompleteNoResponse));
      }
    } else if (error.message.startsWith("Failed to execute 'setItem'")) {
      console.warn("Error saving data to local storage.");
      return (
        caught || of(new RequestState<T>(RequestStatus.IsCompleteNoResponse))
      );
    } else {
      // Server error.
      this.errorHandlingService.sendMessage(MessageType.ServerError);
      console.error(
        `Server error loading data. Status: ${error.status}. Message: `,
        error.error,
      );
    }

    if (cachedResponse) {
      return of(
        new RequestState<T>(
          RequestStatus.HasErrorWithCachedResponse,
          cachedResponse,
        ),
      );
    } else {
      return of(new RequestState<T>(RequestStatus.HasErrorNoResponse));
    }
  }
}
